import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
} from '@mui/material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../context/NotificationsContext';
import { getDashboardStats, getReports, getLifecycleReminders } from '../services/reportService';
import '../components/dashboard/ReportListing.css';
import { BRAND } from '../theme/brandTheme';
import {
  CroKpiCards,
  DashboardHeader,
  BrandOverviewCards,
  DashboardSkeleton,
} from '../components/dashboard';
import PortfolioReport from '../components/dashboard/PortfolioReport';
import showNotification from '../components/common/Notification';
import { LifecycleRemindersBanner } from '../components/dashboard/DecisionRequiredBanner';

/** Dashboard overview — portfolio KPIs and brand cards (reports live on brand pages) */
const Dashboard = () => {
  const { user } = useAuth();
  const { updateReports, lastReportEvent } = useNotifications();
  const [stats, setStats] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [userBrandIds, setUserBrandIds] = useState([]);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [lifecycleReminders, setLifecycleReminders] = useState(null);
  const lastSocketEventRef = useRef(null);
  const nonSilentFetchCountRef = useRef(0);

  const userId = user?._id || user?.id;
  const userRole = user?.role;

  useEffect(() => {
    const newBrandIds = user?.brands ? user.brands.map((brand) => brand._id) : [];
    setUserBrandIds((current) => {
      if (
        current.length === newBrandIds.length &&
        current.every((id, index) => id === newBrandIds[index])
      ) {
        return current;
      }
      return newBrandIds;
    });
  }, [user]);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) nonSilentFetchCountRef.current += 1;

      try {
        if (!silent) setInitialLoading(true);

        const brandIdsForRequest = userRole === 'admin' ? [] : userBrandIds;

        const [statsData, reportsData, remindersData] = await Promise.all([
          getDashboardStats(brandIdsForRequest),
          getReports(brandIdsForRequest),
          getLifecycleReminders().catch(() => null),
        ]);

        const sortedReportsData = (reportsData || []).map((brand) => ({
          ...brand,
          tests: [...(brand.tests || [])].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          ),
        }));

        setStats(statsData);
        setReports(sortedReportsData);
        setLifecycleReminders(remindersData);

        const allReports = sortedReportsData.reduce((acc, brand) => {
          return [
            ...acc,
            ...(brand.tests || []).map((test) => ({
              id: test.id,
              name: test.name,
              createdAt: test.createdAt,
              brand: brand.name,
            })),
          ];
        }, []);
        updateReports(allReports);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        if (!silent) setError('Veriler yüklenirken bir hata oluştu.');
      } finally {
        if (!silent) {
          nonSilentFetchCountRef.current -= 1;
          if (nonSilentFetchCountRef.current <= 0) {
            nonSilentFetchCountRef.current = 0;
            setInitialLoading(false);
          }
        }
      }
    },
    [userRole, userBrandIds, updateReports],
  );

  useEffect(() => {
    if (lastReportEvent?.at) lastSocketEventRef.current = lastReportEvent.at;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    if (userBrandIds.length > 0 || userRole === 'admin') {
      fetchData();
      return;
    }
    setInitialLoading(false);
  }, [user, userId, userRole, userBrandIds, fetchData]);

  useEffect(() => {
    if (!lastReportEvent) return;
    if (lastSocketEventRef.current === lastReportEvent.at) return;

    lastSocketEventRef.current = lastReportEvent.at;
    fetchData({ silent: true });

    const reportLabel = lastReportEvent.data?.reportName || 'Rapor';
    const actionByEvent = {
      'report:created': 'yeni rapor eklendi',
      'report:updated': 'güncellendi',
      'report:deleted': 'silindi',
      'report:statusChanged': 'durumu güncellendi',
    };
    const action = actionByEvent[lastReportEvent.eventName] || 'güncellendi';
    showNotification(`${reportLabel} ${action}`, 'info');
  }, [lastReportEvent, fetchData]);

  if (initialLoading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => fetchData()}>
          Yeniden Dene
        </Button>
      </Box>
    );
  }

  if (userBrandIds.length === 0 && user?.role !== 'admin') {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Henüz size atanmış bir marka bulunmamaktadır.
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Verileri görebilmek için bir yöneticinin size marka ataması gerekmektedir.
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <DashboardHeader user={user} />

      <LifecycleRemindersBanner reminders={lifecycleReminders} />

      <CroKpiCards croKpis={stats?.croKpis} user={user} />

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SummarizeIcon />}
          onClick={() => setPortfolioOpen(true)}
          sx={{ borderColor: BRAND.purple, color: BRAND.purple }}
        >
          Portföy Raporu
        </Button>
        <Button
          component={RouterLink}
          to="/dashboard/statistics"
          variant="outlined"
          size="small"
          startIcon={<BarChartIcon />}
          sx={{ borderColor: BRAND.purple, color: BRAND.purple, textTransform: 'none' }}
        >
          Detaylı İstatistikler
        </Button>
      </Box>

      <BrandOverviewCards brands={reports} />

      <PortfolioReport
        open={portfolioOpen}
        onClose={() => setPortfolioOpen(false)}
        brands={reports}
        croKpis={stats?.croKpis}
      />
    </Container>
  );
};

export default Dashboard;
